import OpenInNew from "@mui/icons-material/OpenInNew";
import { IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { useMemo, useState } from "react";

import { getFacilityName } from "../../utils";
import { COMMUNICATION_FACILITIES_QUERY } from "../hooks/useCommunicationFacilities";
import { CommunicationFacility } from "../types";

interface FacilitiesGridProps {
  tenantId: string;
  initialFacilityIds: string[];
  close: () => void;
  saveFacilities: (facilities: CommunicationFacility[]) => void;
}

export const FacilitiesPickerDialog = ({
  tenantId,
  initialFacilityIds,
  close,
  saveFacilities,
}: FacilitiesGridProps) => {
  const [facilities, setFacilities] = useState<CommunicationFacility[]>([]);
  const [facilityIds, setFacilityIds] = useState<string[]>(initialFacilityIds);

  const columns: GridColDef<CommunicationFacility>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 180,
        filterKeyType: "facility",
        valueGetter: (_, row) => {
          const formattedName = getFacilityName(row);
          return formattedName;
        },
      },
      {
        field: "state",
        headerName: "State/Territory",
        flex: 0.3,
        minWidth: 80,
      },
      {
        field: "streetAddress1",
        headerName: "Street Address",
        flex: 1.2,
        minWidth: 200,
        valueGetter(_, row) {
          if (!row.streetAddress1) return "";
          const { streetAddress1, city, state, zip } = row;
          return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
        },
      },
      {
        field: "lepc",
        headerName: "LEPC",
        flex: 0.8,
        minWidth: 140,
        valueGetter(_, row) {
          return row?.lepc?.name || "";
        },
      },
      {
        field: "fireDepartment",
        headerName: "Fire Department",
        flex: 0.8,
        minWidth: 140,
        valueGetter(_, row) {
          return row?.fireDepartment?.name || "";
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Open Facility">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/o/${tenantId}/facilities/${params.row.id}/profile`, "_blank");
              }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ];
  }, [tenantId]);

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      title="Facilities"
      description="Select one or more facilities"
      primaryActionButtonProps={{
        onClick: () => saveFacilities(facilities),
        saveText: "Done",
        hideStartIcon: true,
        loading: false,
      }}
    >
      <OmnisearchDataGrid
        persistenceKey="facilities-picker"
        columns={columns}
        dataQuery={COMMUNICATION_FACILITIES_QUERY}
        skip={!tenantId}
        initialSortModel={[
          { field: "state", sort: "asc" },
          { field: "facility", sort: "asc" },
        ]}
        defaultSearch={`tenantId:${tenantId}`}
        getItems={(data) => data.facilities.items}
        getCount={(data) => data.facilities.count}
        noDataMessage="No facilities found for this organization."
        isURLDriven={false}
        selectedIds={facilityIds}
        disableRowSelectionOnClick={false}
        onSelectedIdsChanged={(newFacilityIds) => {
          setFacilityIds(newFacilityIds as string[]);
        }}
        onSelectedRowsChanged={(newFacilities) => {
          setFacilities(newFacilities);
        }}
        initialPageSize={10}
        withPadding={false}
        showManageColumns={false}
        tenantId={tenantId}
      />
    </Dialog>
  );
};
